import type { BreedingInventorySnapshot } from "./liveBreedingInventory";

export interface PairFormationSourceOption {
  stockGroupId: string;
  stockVersion: number;
  fryBatchId: string;
  tankId: string;
  tankCode: string;
  quantity: number;
  quantityPrecision: "estimated" | "exact";
  groupCode?: string;
  batchCode?: string;
}

export type PairFormationValidationCode =
  | "PAIR_DESTINATION_OCCUPIED"
  | "PAIR_SOURCE_REQUIRED"
  | "PAIR_VARIETY_REQUIRED"
  | "PAIR_SOURCE_QUANTITY_INSUFFICIENT"
  | "PAIR_ONLINE_REQUIRED";

export type PairFormationValidationResult =
  | { ok: true }
  | { ok: false; code: PairFormationValidationCode };

export interface PairFormationSelectionInput {
  destinationTankEmpty: boolean;
  maleStockGroupId: string;
  femaleStockGroupId: string;
  maleVariety: string;
  femaleVariety: string;
  quantities: ReadonlyMap<string, number>;
}

export interface PairFormationSummaryInput {
  destinationTankCode: string;
  maleSource: PairFormationSourceOption;
  maleVariety: string;
  femaleSource: PairFormationSourceOption;
  femaleVariety: string;
}

export interface PairFormationSummary {
  destination: string;
  male: string;
  female: string;
  decrements: string[];
  parts: string[];
}

export function destinationTankIsEmpty(
  snapshot: BreedingInventorySnapshot,
  tankId: string,
): boolean {
  return !snapshot.occupancies.some((occupancy) => occupancy.tankId === tankId);
}

export function eligiblePairFormationSources(
  snapshot: BreedingInventorySnapshot,
): PairFormationSourceOption[] {
  return snapshot.stockGroups.flatMap((stock) => {
    if (
      stock.status !== "active" ||
      stock.quantityPrecision === "unknown" ||
      stock.quantity === undefined ||
      stock.quantity <= 0
    ) {
      return [];
    }

    const occupancy = snapshot.occupancies.find(
      (item) =>
        item.occupantKind === "stock_group" &&
        item.stockGroupId === stock.id &&
        Boolean(item.tankId),
    );
    if (!occupancy) return [];

    const tank = snapshot.tanks.find((item) => item.id === occupancy.tankId && item.active);
    if (!tank) return [];

    const batch = snapshot.fryBatches.find((item) => item.id === stock.fryBatchId);

    return [
      {
        stockGroupId: stock.id,
        stockVersion: stock.domainVersion,
        fryBatchId: stock.fryBatchId,
        tankId: tank.id,
        tankCode: tank.tankCode,
        quantity: stock.quantity,
        quantityPrecision: stock.quantityPrecision,
        ...(stock.groupCode ? { groupCode: stock.groupCode } : {}),
        ...(batch?.batchCode ? { batchCode: batch.batchCode } : {}),
      },
    ];
  });
}

export function requiredSourceCounts(
  maleStockGroupId: string,
  femaleStockGroupId: string,
): Map<string, number> {
  const counts = new Map<string, number>();
  if (maleStockGroupId) counts.set(maleStockGroupId, 1);
  if (femaleStockGroupId) {
    counts.set(femaleStockGroupId, (counts.get(femaleStockGroupId) ?? 0) + 1);
  }
  return counts;
}

export function validatePairFormationSelection(
  input: PairFormationSelectionInput,
): PairFormationValidationResult {
  if (!input.destinationTankEmpty) {
    return { ok: false, code: "PAIR_DESTINATION_OCCUPIED" };
  }
  if (!input.maleStockGroupId || !input.femaleStockGroupId) {
    return { ok: false, code: "PAIR_SOURCE_REQUIRED" };
  }
  if (!input.maleVariety.trim() || !input.femaleVariety.trim()) {
    return { ok: false, code: "PAIR_VARIETY_REQUIRED" };
  }

  for (const [stockGroupId, required] of requiredSourceCounts(
    input.maleStockGroupId,
    input.femaleStockGroupId,
  )) {
    const quantity = input.quantities.get(stockGroupId);
    if (quantity === undefined || quantity < required) {
      return { ok: false, code: "PAIR_SOURCE_QUANTITY_INSUFFICIENT" };
    }
  }

  return { ok: true };
}

function persianNumber(value: number): string {
  return value.toLocaleString("fa-IR");
}

export function buildPairFormationSummary(input: PairFormationSummaryInput): PairFormationSummary {
  const counts = requiredSourceCounts(
    input.maleSource.stockGroupId,
    input.femaleSource.stockGroupId,
  );

  const sourceById = new Map([
    [input.maleSource.stockGroupId, input.maleSource],
    [input.femaleSource.stockGroupId, input.femaleSource],
  ]);

  const decrements = [...counts.entries()].map(([stockGroupId, count]) => {
    const source = sourceById.get(stockGroupId);
    return `${source ? `تانک ${source.tankCode}` : "گله انتخاب‌شده"}: -${persianNumber(count)}`;
  });

  const destination = input.destinationTankCode;
  const male = `${input.maleVariety.trim()} · تانک ${input.maleSource.tankCode}`;
  const female = `${input.femaleVariety.trim()} · تانک ${input.femaleSource.tankCode}`;

  return {
    destination,
    male,
    female,
    decrements,
    parts: [
      `تانک مقصد: ${destination}`,
      `نر: ${input.maleVariety.trim()}، از تانک ${input.maleSource.tankCode}`,
      `ماده: ${input.femaleVariety.trim()}، از تانک ${input.femaleSource.tankCode}`,
      `کسر موجودی: ${decrements.join("، ")}`,
      "کد جفت به صورت خودکار ساخته می‌شود",
    ],
  };
}

export function validatePairFormationConfirmation(
  input: PairFormationSelectionInput & { online: boolean },
): PairFormationValidationResult {
  const selection = validatePairFormationSelection(input);
  if (!selection.ok) return selection;
  if (!input.online) return { ok: false, code: "PAIR_ONLINE_REQUIRED" };
  return { ok: true };
}
